import { useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShapeThumb } from "@/components/settings/ShapeThumb";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";
import { SHAPE_STYLES } from "@/lib/components";

const cell = inputCls.replace("w-full", "");

function Row({ s, i, lines, onChange }) {
  const names = lines.filter((l) => s.line_ids.includes(l.id)).map((l) => l.name);
  return (
    <tr data-testid={`supplier-shape-row-${i}`} className="border-b border-slate-800/70 align-middle">
      <td className="px-2 py-2"><input type="checkbox" data-testid={`supplier-shape-include-${i}`} checked={s.include} onChange={(e) => onChange({ include: e.target.checked })} /></td>
      <td className="px-2 py-2"><ShapeThumb src={s.image} shape={{ name: s.name }} className="h-14 w-14" testId={`supplier-shape-picture-${i}`} /></td>
      <td className="px-2 py-2"><input data-testid={`supplier-shape-name-${i}`} value={s.name} onChange={(e) => onChange({ name: e.target.value })} className={`${cell} w-72`} />
        {s.exists && <span className="ml-2 rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">already saved · keeps your edits</span>}</td>
      <td className="px-2 py-2">
        <select data-testid={`supplier-shape-style-${i}`} value={s.style} onChange={(e) => onChange({ style: e.target.value })} className={`${cell} w-40`}>
          {SHAPE_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </td>
      <td className="whitespace-nowrap px-2 py-2 text-right">
        <input data-testid={`supplier-shape-price-${i}`} inputMode="decimal" value={s.price} onChange={(e) => onChange({ price: e.target.value })} className={`${cell} w-20 text-right font-mono-num`} />
        <span className="ml-1 text-xs text-slate-500">/sq ft</span>
      </td>
      <td className="px-2 py-2 text-xs text-slate-400" title={names.join(", ")}>{names.length} product lines</td>
    </tr>
  );
}

export function SupplierShapesDialog({ lines, onClose, onDone }) {
  const [res, setRes] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const read = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/shapes/supplier/preview", fd);
      setRes(data);
      setRows(data.shapes.map((s) => ({ ...s, price: Math.round(s.price * 10000) / 10000, include: true })));
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const shapes = rows.filter((r) => r.include).map(({ include, exists, ...r }) => ({ ...r, price: Number(r.price) || 0 }));
      const { data } = await api.post("/settings/shapes/supplier", { shapes });
      toast.success(`Saved ${data.saved} shapes`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const patch = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="supplier-shapes-dialog" className="max-h-[92vh] max-w-5xl overflow-y-auto bg-slate-950">
        <DialogHeader>
          <DialogTitle className="font-display text-slate-50">Load shapes from supplier list</DialogTitle>
          <DialogDescription className="text-slate-400">Reads the custom-shape pictures and prices from the supplier .xlsx. Rename, restyle or re-price before saving.</DialogDescription>
        </DialogHeader>
        {!res ? (
          <label data-testid="supplier-shapes-drop" className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-slate-700 p-10 text-sm text-slate-300 hover:border-amber-500/60">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <FileSpreadsheet className="h-6 w-6 text-amber-300" />}
            Choose the supplier .xlsx
            <input data-testid="supplier-shapes-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => read(e.target.files[0])} />
          </label>
        ) : (
          <>
            <p data-testid="supplier-shapes-unit-note" className="text-xs text-slate-400">{res.unit_note} You can change the product lines each shape applies to after saving.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="supplier-shapes-preview">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                  <tr><th /><th className="px-2">Picture</th><th className="px-2">Name</th><th className="px-2">Drawing</th><th className="px-2 text-right">Price</th><th className="px-2">Applies to</th></tr>
                </thead>
                <tbody>{rows.map((s, i) => <Row key={s.row} s={s} i={i} lines={lines} onChange={(p) => patch(i, p)} />)}</tbody>
              </table>
            </div>
          </>
        )}
        <DialogFooter>
          <button data-testid="supplier-shapes-cancel" onClick={onClose} disabled={busy} className="rounded-full px-4 py-2 text-sm text-slate-300 hover:text-slate-50">Cancel</button>
          {res && (
            <button data-testid="supplier-shapes-confirm" onClick={confirm} disabled={busy || !rows.some((r) => r.include)}
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-40">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save {rows.filter((r) => r.include).length} shapes
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
